import { useCallback, useState } from "react";
import { evaluate } from "mathjs";
import { useFormulas } from "../../../hooks/query";
import { FORMULA_EVAL_ORDER } from "../../../constants/formulaEvalOrder";
import { buildSelectionsScope, resolveFormulaSubcategoryKey } from "../../../utils/formulaScope";

// Define measurements type based on actual usage (array of objects)
type MeasurementValues = any[];

interface PrefilledValues {
    [product: string]: {
        value: string;
        quantity?: number;
    };
}

const useFormulaEvaluator = () => {
    const [computedValues, setComputedValues] = useState<Record<string, number | null>>({});
    const { useList } = useFormulas();
    const { data: formulas, isLoading: isLoadingFormulas } = useList();

    const evaluateFormula = useCallback(
        (
            measurements: MeasurementValues,
            prefilledValues: PrefilledValues,
            attributes?: any[],
        ): Record<string, number | null> => {
            const results: Record<string, number | null> = {};

            // Return empty results if formulas are not loaded yet
            if (!formulas || isLoadingFormulas) {
                return results;
            }

            const orderedProducts = [
                ...FORMULA_EVAL_ORDER.filter((p) => p in formulas),
                ...Object.keys(formulas).filter((p) => !FORMULA_EVAL_ORDER.includes(p)),
            ];

            const measurementsObject = convertMeasurementIntoKeyValuePair(measurements);
            const attributesScope = convertAttributesIntoScope(attributes);
            const selectionsScope = buildSelectionsScope(prefilledValues);

            for (const product of orderedProducts) {
                const productFormula = formulas[product];

                let expression: string | undefined;
                let outputFormat: "WholeNumber" | "Decimal" | undefined;

                if (productFormula.subcategories) {
                    const subKey = resolveFormulaSubcategoryKey(
                        product,
                        prefilledValues[product]?.value || "",
                        prefilledValues,
                    );
                    const subcategoryFormula = productFormula.subcategories[subKey];
                    if (subcategoryFormula) {
                        expression = subcategoryFormula.expression;
                        outputFormat = subcategoryFormula.outputFormat;
                    }
                }

                if (!expression && productFormula.expression) {
                    expression = productFormula.expression;
                    outputFormat = productFormula.outputFormat;
                }

                if (expression) {
                    try {
                        const resultsScope: Record<string, any> = {};
                        Object.entries(results).forEach(([prod, val]) => {
                            if (val !== null) {
                                resultsScope[prod.replace(/\s+/g, "")] = val;
                            }
                        });

                        const scope = {
                            ...measurementsObject,
                            ...attributesScope,
                            ...selectionsScope,
                            ...resultsScope,
                        };

                        let result = evaluate(expression, scope) as number;

                        if (outputFormat === "WholeNumber") {
                            result = Math.ceil(result);
                        }

                        results[product] = result;
                    } catch (error: any) {
                        if (error && error.message && error.message.includes("Undefined symbol")) {
                            // missing measurement — skip
                        } else {
                            console.error(`Error evaluating formula for ${product}:`, error);
                        }
                        results[product] = null;
                    }
                }
            }

            setComputedValues(results);
            return results; // Return computed values immediately
        },
        [formulas, isLoadingFormulas],
    );

    const convertMeasurementIntoKeyValuePair = (measurements: any) => {
        return measurements.reduce((acc: any, measurement: any) => {
            // Use ActualValue if available, otherwise fall back to CalculatedValue (predicted value)
            const value = measurement.ActualValue ?? measurement.CalculatedValue;

            // Try updating to number, but if it fails (NaN), keep as original string (unless it is empty/null)
            const numVal = Number(value);
            if (!isNaN(numVal) && value !== "" && value !== null && value !== undefined) {
                acc[measurement.MeasurementType] = numVal;
            } else {
                acc[measurement.MeasurementType] = value;
            }
            return acc;
        }, {});
    };

    /**
     * Converts inspection_attributes rows into a flat mathjs scope.
     * - Plain scalars ("6/12", "Yes", "1") → injected as-is (number if parseable)
     * - JSON arrays ([{size,count}]) → collapsed to total count (sum of all count fields)
     * Variable names are injected both raw ("Roof Pitch") and camelCase ("RoofPitch").
     */
    const convertAttributesIntoScope = (attributes?: any[]): Record<string, any> => {
        if (!attributes || attributes.length === 0) return {};
        const scope: Record<string, any> = {};

        attributes.forEach((attr) => {
            const rawName: string = attr.AttributeName;
            if (!rawName) return;

            // camelCase key: Remove special characters then "Roof Pitch" → "RoofPitch"
            const sanitizedName = rawName.replace(/[^a-zA-Z0-9\s]/g, '');
            const camelKey = sanitizedName.replace(/\s+(\w)/g, (_: string, c: string) => c.toUpperCase());

            const raw = attr.AttributeValue;
            if (raw === null || raw === undefined || raw === "") return;

            // Attempt JSON parse first (handles array attributes like [{size,count}])
            let parsed: any;
            try {
                parsed = JSON.parse(raw);
            } catch {
                parsed = null;
            }

            let value: any;
            if (Array.isArray(parsed)) {
                // Sum all count fields, default to array length if no count field
                const total = parsed.reduce((sum: number, item: any) => sum + (Number(item.count) || 0), 0);
                value = total > 0 ? total : parsed.length;
            } else {
                // Plain string — convert to number if possible, also parse fractions like "6/12"
                let num = Number(raw);
                if (isNaN(num) && typeof raw === "string" && raw.includes("/")) {
                    const parts = raw.split("/");
                    if (parts.length === 2) {
                        const num1 = Number(parts[0].trim());
                        const num2 = Number(parts[1].trim());
                        if (!isNaN(num1) && !isNaN(num2) && num2 !== 0) {
                            num = num1 / num2;
                        }
                    }
                }
                value = !isNaN(num) && raw !== "" ? num : raw;
            }

            scope[rawName] = value;
            scope[camelKey] = value;
        });

        return scope;
    };

    return { computedValues, evaluateFormula };
};

export default useFormulaEvaluator;
